import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Minus, Plus, ShoppingBag, Heart, Truck, Shield, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useCart } from '@/store/useCart';
import { ProductCard } from '@/components/product/ProductCard';

interface Product {
    id: string;
    name: string;
    slug: string;
    description?: string;
    price: string | number;
    compareAtPrice?: string | number;
    salePrice?: number;
    sku?: string;
    stock: number;
    images: string[];
    isFeatured: boolean;
    category?: {
        id: string;
        name: string;
        slug: string;
    };
    relatedProducts?: Product[];
}

export function ProductDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const { addItem } = useCart();

    // Fetch product data
    const { data: product, isLoading, error } = useQuery({
        queryKey: ['product', slug],
        queryFn: () => api.get<Product>(`/products/${slug}`),
        enabled: !!slug,
    });

    // Calculate discount percentage
    const getDiscountPercentage = () => {
        if (!product?.compareAtPrice || !product?.price) return 0;
        const original = parseFloat(String(product.compareAtPrice));
        const current = parseFloat(String(product.price));
        if (original <= current) return 0;
        return Math.round(((original - current) / original) * 100);
    };

    const handleAddToCart = () => {
        if (!product) return;
        addItem({
            id: product.id,
            name: product.name,
            price: parseFloat(String(product.price)),
            image: product.images?.[0] || '',
            quantity,
        });
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="section animate-fade-in">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12">
                        <div className="aspect-square rounded-3xl bg-muted animate-pulse" />
                        <div className="space-y-6">
                            <div className="h-8 bg-muted rounded animate-pulse w-3/4" />
                            <div className="h-12 bg-muted rounded animate-pulse w-1/2" />
                            <div className="h-24 bg-muted rounded animate-pulse" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !product) {
        return (
            <div className="section animate-fade-in">
                <div className="container mx-auto px-4">
                    <div className="text-center py-20">
                        <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
                        <h1 className="text-2xl font-bold mb-2">Product Not Found</h1>
                        <p className="text-muted-foreground mb-6">
                            The product you're looking for doesn't exist or has been removed.
                        </p>
                        <Link to="/products" className="btn-primary">
                            Browse Products
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const discountPercentage = getDiscountPercentage();
    const isInStock = product.stock > 0;
    const images = product.images?.length > 0 ? product.images : [];

    return (
        <div className="section animate-fade-in">
            <div className="container mx-auto px-4">
                {/* Breadcrumb */}
                <Link
                    to="/products"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back to Products
                </Link>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Product Images */}
                    <div className="space-y-4">
                        {/* Main Image */}
                        <div className="aspect-square rounded-3xl bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center overflow-hidden">
                            {images.length > 0 ? (
                                <img
                                    src={images[selectedImage]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span className="text-muted-foreground text-2xl">No Image</span>
                            )}
                        </div>

                        {/* Thumbnail Gallery */}
                        {images.length > 1 && (
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                {images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${selectedImage === index
                                            ? 'border-primary ring-2 ring-primary/20'
                                            : 'border-transparent hover:border-muted-foreground/30'
                                            }`}
                                    >
                                        <img
                                            src={img}
                                            alt={`${product.name} ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        <div>
                            <span className={`badge ${isInStock ? 'badge-primary' : 'bg-destructive text-destructive-foreground'} mb-3`}>
                                {isInStock ? 'In Stock' : 'Out of Stock'}
                            </span>
                            <h1 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-2">
                                {product.name}
                            </h1>
                            <p className="text-muted-foreground">
                                {product.sku && `SKU: ${product.sku} • `}
                                {product.category && `Category: ${product.category.name}`}
                            </p>
                        </div>

                        {/* Price */}
                        <div className="flex items-baseline gap-3">
                            <span className="text-3xl font-bold text-primary">
                                ${parseFloat(String(product.price)).toFixed(2)}
                            </span>
                            {product.compareAtPrice && parseFloat(String(product.compareAtPrice)) > parseFloat(String(product.price)) && (
                                <>
                                    <span className="text-xl text-muted-foreground line-through">
                                        ${parseFloat(String(product.compareAtPrice)).toFixed(2)}
                                    </span>
                                    <span className="badge badge-accent">{discountPercentage}% OFF</span>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        {product.description && (
                            <p className="text-muted-foreground leading-relaxed">
                                {product.description}
                            </p>
                        )}

                        {/* Stock Info */}
                        {isInStock && product.stock <= 10 && (
                            <p className="text-sm text-amber-600 dark:text-amber-400">
                                Only {product.stock} left in stock - order soon!
                            </p>
                        )}

                        {/* Quantity */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Quantity</label>
                            <div className="flex items-center gap-3">
                                <button
                                    className="btn-outline p-2"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    disabled={!isInStock}
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span className="w-12 text-center font-semibold">{quantity}</span>
                                <button
                                    className="btn-outline p-2"
                                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                                    disabled={!isInStock || quantity >= product.stock}
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4">
                            <button
                                className="btn-primary btn-lg flex-1"
                                onClick={handleAddToCart}
                                disabled={!isInStock}
                            >
                                <ShoppingBag className="h-5 w-5" />
                                {isInStock ? 'Add to Cart' : 'Out of Stock'}
                            </button>
                            <button className="btn-outline p-4">
                                <Heart className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Trust Badges */}
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-border">
                            <div className="flex items-center gap-3">
                                <Truck className="h-5 w-5 text-primary" />
                                <div>
                                    <p className="text-sm font-medium text-foreground">Free Shipping</p>
                                    <p className="text-xs text-muted-foreground">On orders over $50</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Shield className="h-5 w-5 text-primary" />
                                <div>
                                    <p className="text-sm font-medium text-foreground">30-Day Returns</p>
                                    <p className="text-xs text-muted-foreground">Hassle-free returns</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                {product.relatedProducts && product.relatedProducts.length > 0 && (
                    <div className="mt-20">
                        <h2 className="text-2xl font-display font-bold text-foreground mb-8">
                            Related Products
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {product.relatedProducts.map((relatedProduct) => (
                                <ProductCard
                                    key={relatedProduct.id}
                                    product={{
                                        ...relatedProduct,
                                        price: parseFloat(String(relatedProduct.price)),
                                        salePrice: relatedProduct.compareAtPrice
                                            ? parseFloat(String(relatedProduct.price))
                                            : undefined,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
